import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, Plus, Trash2 } from 'lucide-react';
import ModuleIcon from '../../components/public/ModuleIcon';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { formatDate } from '../../utils/format';
import MessageDetailModal from './MessageDetailModal';
import { sentTo } from './sentTo';
import { listSecrets, revokeSecret, secretKeys } from './secretsApi';
import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';

export default function SentMessagesPage() {
  const { allows } = useTenant();
  const canCreate = allows(ACTIONS.SECRET_CREATE);
  const canDelete = allows(ACTIONS.SECRET_DELETE);
  const queryClient = useQueryClient();
  const [viewId, setViewId] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: secretKeys.list(),
    queryFn: () => listSecrets(),
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => revokeSecret(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] });
      toast.success('Message destroyed');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not destroy the message')),
  });

  const rows = data?.data ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ModuleIcon slug="secure-drop" decorative className="h-7 w-7" />
          <h1 className="text-xl font-semibold text-slate-800">Secure Drop</h1>
        </div>
        {canCreate && <Link to="/messages">
          <Button size="sm">
            <Plus className="h-4 w-4" />
            New message
          </Button>
        </Link>}
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-red-600">{getErrorMessage(error, 'Could not load messages')}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          You haven’t sent any encrypted messages yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="whitespace-nowrap bg-slate-100 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Label</th>
                <th className="px-4 py-3 font-medium">Sent to</th>
                <th className="px-4 py-3 font-medium">Views</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => (
                <tr key={s.id} className={s.destroyedAt ? 'text-slate-400' : 'text-slate-700'}>
                  <td className="px-4 py-3 font-medium">{s.name}</td>
                  <td
                    className={`max-w-[22rem] truncate px-4 py-3${s.recipientEmail ? '' : ' text-slate-400'}`}
                    title={s.recipientEmail || s.deliveredBy ? sentTo(s) : 'Sent before recipients were recorded'}
                  >
                    {sentTo(s)}
                  </td>
                  <td className="px-4 py-3">
                    {s.viewsCount}
                    {s.maxViews ? ` / ${s.maxViews}` : ''}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{s.expiresAt ? formatDate(s.expiresAt) : '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3">{formatDate(s.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setViewId(s.id)}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
                        title="View status and access log"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                      {s.destroyedAt ? (
                        <span className="px-2 text-xs text-slate-400">Destroyed</span>
                      ) : canDelete ? (
                        <button
                          onClick={() => revokeMutation.mutate(s.id)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50"
                          title="Destroy now"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Destroy
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MessageDetailModal open={Boolean(viewId)} onClose={() => setViewId(null)} secretId={viewId} />
    </div>
  );
}
